import React, { useState } from 'react';
import { IonButton, IonText } from '@ionic/react';
import { ProductGroupType, ProductModelSearchResult } from 'core/types';
import ProductModelSearchSelect from 'features/shared/inventory/components/ProductModelSearchSelect';
import 'components/modal/HkModal.scss';
import './InventoryAddItemLanding.scss';

interface InventoryAddItemLandingProps {
  productTypeGroupsList: ProductGroupType[];
  onModelMatched: (
    groupId: number,
    typeId: number,
    result: ProductModelSearchResult
  ) => void;
  onManualAdd: () => void;
}

/**
 * "Add an Item" landing (Figma node 8687:5876): an "Enter Item Model" search
 * against the homeowner product-model catalog as the primary path, with a
 * "Manual Add Item" fallback. A selected catalog model carries its own
 * `type_id`, which is looked up in the Group list by id to jump straight into
 * the existing Group/Type driven manual flow (photo capture + attribute
 * schema). The full search result (not just the Group/Type match) is passed up
 * so the manual form can also prefill Model # and Brand/Manufacturer from it
 * (D20).
 *
 * Renders content only (`.hk-modal-content`, a class `components/modal/
 * HkModal.scss` defines) — no header, no footer. The header is rendered
 * once, unconditionally, by InventoryAddProduct.tsx so it stays identical
 * across the landing → manual-form transition; this is embedded inside
 * InventoryAddProduct's already-open outer IonModal (shared with that
 * manual-form step), so it must not open a second, nested IonModal either.
 * The Cancel footer also lives in InventoryAddProduct.tsx, as a sibling of
 * IonContent (not nested inside it, like this component is) — so it stays
 * pinned in place rather than scrolling away with this content.
 */
const InventoryAddItemLanding: React.FC<InventoryAddItemLandingProps> = ({
  productTypeGroupsList,
  onModelMatched,
  onManualAdd
}) => {
  const [noMatchWarning, setNoMatchWarning] = useState(false);

  // AGENT NOTE: resolve the picked model's Product Type by `result.type_id`,
  // never by comparing `result.type` to a local `label`. Those labels are
  // admin-editable display text — a rename in the admin catalog would silently
  // stop every model from matching, with no error to trace.
  const onSelectModel = (result: ProductModelSearchResult) => {
    setNoMatchWarning(false);
    for (const group of productTypeGroupsList) {
      const match = group.product_template_types?.find(
        (t) => t.id === result.type_id
      );
      if (match) {
        onModelMatched(group.id, match.id, result);
        return;
      }
    }
    // The model's product type isn't in any Group this homeowner can add to —
    // fall back to manual Group/Type selection rather than blocking them.
    setNoMatchWarning(true);
  };

  return (
    <div className="hk-modal-content">
      <ProductModelSearchSelect
        className="hk-inventory-add-item-landing-search"
        variant="search"
        placeholder="Enter Item Model"
        onChange={onSelectModel}
      />
      <IonText className="hk-inventory-add-item-landing-helper">
        Enter a <strong>model number</strong> to get started, or you can
        manually add an item and add your model later.
      </IonText>
      {noMatchWarning && (
        <IonText color="warning">
          <p className="hk-inventory-add-item-landing-warning" role="alert">
            We couldn&rsquo;t match that model to a product type automatically —
            continue with Manual Add Item below.
          </p>
        </IonText>
      )}
      <div className="hk-inventory-add-item-landing-divider">
        <span>or</span>
      </div>
      <IonButton
        expand="block"
        className="hk-inventory-add-item-landing-manual-button"
        color="primary"
        onClick={onManualAdd}
      >
        Manual Add Item
      </IonButton>
    </div>
  );
};

export default InventoryAddItemLanding;
